// Bidirectional converter between Resume Architect's internal format
// and the JSON Resume schema (https://jsonresume.org/schema/).
// Lets users import resumes exported from Reactive Resume, OpenResume,
// jsonresume.org, etc., and export theirs as the cross-tool standard.

const splitLocation = (loc) => {
    if (!loc || typeof loc !== 'string') return { city: '', region: '', countryCode: '' };
    const parts = loc.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length === 0) return { city: '', region: '', countryCode: '' };
    if (parts.length === 1) return { city: parts[0], region: '', countryCode: '' };
    return { city: parts[0], region: parts[1], countryCode: parts[2] || '' };
};

const joinLocation = (locObj) => {
    if (!locObj) return '';
    if (typeof locObj === 'string') return locObj;
    return [locObj.city, locObj.region, locObj.countryCode].filter(Boolean).join(', ');
};

// Split a multi-line / bullet description into clean highlights[]
const descriptionToHighlights = (desc) => {
    if (!desc) return [];
    return desc
        .split(/\r?\n/)
        .map(l => l.replace(/^[\s•\-*·]+/, '').trim())
        .filter(Boolean);
};

const highlightsToDescription = (highlights) => {
    if (!highlights || !highlights.length) return '';
    return highlights.map(h => `• ${h}`).join('\n');
};

// Convert internal resumeData -> JSON Resume v1.0.0
export const toJsonResume = (resumeData) => {
    const p = resumeData.personal || {};
    return {
        $schema: "https://raw.githubusercontent.com/jsonresume/resume-schema/v1.0.0/schema.json",
        basics: {
            name: p.fullName || '',
            email: p.email || '',
            phone: p.phone || '',
            summary: p.summary || '',
            location: splitLocation(p.location)
        },
        work: (resumeData.experience || []).map(e => ({
            name: e.company || '',
            position: e.role || '',
            location: e.location || '',
            startDate: e.startDate || '',
            endDate: e.endDate || '',
            summary: '',
            highlights: descriptionToHighlights(e.description)
        })),
        education: (resumeData.education || []).map(ed => ({
            institution: ed.school || '',
            area: ed.degree || '',
            studyType: '',
            startDate: '',
            endDate: ed.year || '',
            location: ed.location || ''
        })),
        skills: (resumeData.skills || []).map(s => ({ name: s, level: '', keywords: [] })),
        meta: {
            customSections: resumeData.customSections || [],
            sectionOrder: resumeData.sectionOrder || [],
            exportedBy: 'Resume Architect',
            version: '1.0.0'
        }
    };
};

// Convert JSON Resume -> internal resumeData
export const fromJsonResume = (jr) => {
    const basics = jr.basics || {};
    const meta = jr.meta || {};
    return {
        personal: {
            fullName: basics.name || '',
            email: basics.email || '',
            phone: basics.phone || '',
            location: joinLocation(basics.location),
            summary: basics.summary || ''
        },
        experience: (jr.work || []).map((w, i) => ({
            id: Date.now() + i,
            role: w.position || '',
            company: w.name || w.company || '',
            location: typeof w.location === 'string' ? w.location : joinLocation(w.location),
            startDate: w.startDate || '',
            endDate: w.endDate || '',
            description: highlightsToDescription(w.highlights) || w.summary || ''
        })),
        education: (jr.education || []).map((ed, i) => ({
            id: Date.now() + 1000 + i,
            degree: [ed.studyType, ed.area].filter(Boolean).join(' in ') || ed.area || '',
            school: ed.institution || '',
            location: typeof ed.location === 'string' ? ed.location : joinLocation(ed.location),
            year: ed.endDate || ed.startDate || ''
        })),
        skills: (jr.skills || []).map(s => typeof s === 'string' ? s : s.name).filter(Boolean),
        customSections: meta.customSections || [],
        sectionOrder: meta.sectionOrder && meta.sectionOrder.length
            ? meta.sectionOrder
            : ['experience', 'education', 'skills']
    };
};

// Auto-detect format and parse. Accepts:
//   - JSON Resume schema (has `basics` key)
//   - Resume Architect internal format (has `personal` key)
const isJsonResume = (obj) => obj && typeof obj === 'object' && obj.basics && !obj.personal;
const isInternal = (obj) => obj && typeof obj === 'object' && obj.personal;

export const detectAndParse = (jsonText) => {
    let obj;
    try {
        obj = JSON.parse(jsonText);
    } catch (e) {
        throw new Error('Invalid JSON file.');
    }
    if (isJsonResume(obj)) return { data: fromJsonResume(obj), format: 'json-resume' };
    if (isInternal(obj)) return { data: obj, format: 'internal' };
    throw new Error('Unrecognized resume format. Expected JSON Resume schema or Resume Architect format.');
};
